/**
 * Singleton Web Audio analyser with onset/impulse detection.
 *
 * connectAnalyser(audioEl) — idempotent. Safe to call repeatedly with
 * the same element (e.g. on every track change in a persistent-audio
 * pattern). Only rebuilds the graph when needed.
 *
 * Call tickPulse() every rAF frame — returns { onset }.
 * `onset` is true only on the frame an impulse fires; the UI
 * toggles a CSS class and lets CSS transitions handle the decay.
 */
let ctx, analyser, source, connectedEl;
const sourceByElement = new WeakMap();
const buf = new Uint8Array(256);

export function connectAnalyser(audioEl) {
  if (!audioEl) return;

  // Idempotent: same element + graph already wired — just resume context
  if (audioEl === connectedEl && source) {
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    return;
  }

  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.connect(ctx.destination);
  }

  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }

  let nextSource = sourceByElement.get(audioEl);

  try {
    if (!nextSource) {
      nextSource = ctx.createMediaElementSource(audioEl);
      sourceByElement.set(audioEl, nextSource);
    }

    if (source && source !== nextSource) {
      try {
        source.disconnect();
      } catch (_) {}
    }

    try {
      nextSource.disconnect();
    } catch (_) {}

    nextSource.connect(analyser);
    source = nextSource;
    connectedEl = audioEl;
  } catch (_) {
    // Source creation/connection failed — leave previous graph intact.
  }
}

// ── Tuning knobs ──────────────────────────────────────────────────
const SILENCE = 0.04; // ignore signal below this
const EMA_ALPHA = 0.03; // baseline tracking speed (lower = slower)
const TRIGGER = 1.5; // peak must exceed baseline × this to fire
const DEBOUNCE = 400; // min ms between onsets
const STARVE_MS = 2000; // lower trigger ratio after this many ms of silence
const MIN_TRIGGER = 1.05; // starvation floor for trigger ratio

// ── Internal state ────────────────────────────────────────────────
let baseline = 0; // EMA of recent peak amplitude
let prevPeak = 0; // previous frame's raw peak (for rising-edge)
let lastFireMs = 0; // timestamp of last onset trigger
let ratio = TRIGGER; // adaptive trigger ratio

// ── Per-frame cache ───────────────────────────────────────────────
// Multiple Waveform instances (track-level + main playbar) each run
// their own rAF loop and call tickPulse(). Without caching, the
// second caller in the same frame could miss onsets (already consumed
// by the first caller's debounce gate). We compute once per frame and
// hand every caller the same result.
let cachedFrame = -1;
let cachedResult = { onset: false };

/** Read raw peak amplitude from the analyser (0–1). */
function rawPeak() {
  if (!analyser) return 0;
  analyser.getByteTimeDomainData(buf);
  let max = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = Math.abs(buf[i] - 128) / 128;
    if (v > max) max = v;
  }
  return max;
}

/** Internal — run the actual onset-detection logic (once per frame). */
function computeOnset() {
  const peak = rawPeak();

  // Silence gate — don't pollute baseline with noise floor
  if (peak < SILENCE) {
    prevPeak = peak;
    return { onset: false };
  }

  // Update adaptive baseline (EMA)
  baseline = baseline * (1 - EMA_ALPHA) + peak * EMA_ALPHA;

  // Starvation: if nothing has fired for a while, ease trigger down
  const now = performance.now();
  const gap = now - lastFireMs;
  if (gap > STARVE_MS) {
    ratio = Math.max(MIN_TRIGGER, ratio - 0.002);
  }

  // Onset detection: rising edge + exceeds baseline × ratio
  const isRising = peak > prevPeak;
  const isAbove = peak > baseline * ratio;

  let onset = false;
  if (isRising && isAbove && now - lastFireMs > DEBOUNCE) {
    onset = true;
    lastFireMs = now;
    ratio = TRIGGER; // snap ratio back on successful fire
  }

  prevPeak = peak;
  return { onset };
}

/**
 * Call once per animation frame from each Waveform instance.
 * Returns { onset } — onset is true only on the frame an impulse fires.
 *
 * Safe to call from multiple rAF loops in the same frame — the
 * analyser is sampled at most once; every caller gets the same result.
 */
export function tickPulse() {
  // requestAnimationFrame callbacks within the same frame share a
  // timestamp, but we don't receive it here. Use a monotonic counter
  // bumped by rAF instead. Approximation: performance.now() quantised
  // to 2ms — well within a single 16.7ms frame.
  const now = Math.round(performance.now() * 0.5);
  if (now !== cachedFrame) {
    cachedFrame = now;
    cachedResult = computeOnset();
  }
  return cachedResult;
}

/** Reset onset state (call on track change / play start).
 *  Debounced: multiple Waveform instances may mount their pulse
 *  effects within the same microtask; only the first reset matters. */
let lastResetMs = 0;
export function resetPulse() {
  const now = performance.now();
  if (now - lastResetMs < 50) return; // already reset this frame
  lastResetMs = now;
  baseline = 0;
  prevPeak = 0;
  lastFireMs = 0;
  ratio = TRIGGER;
  cachedFrame = -1; // force recompute on next tick
  cachedResult = { onset: false };
}
